import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { tvSeriesData } from '@/mocks/tvSeries';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const series = tvSeriesData.find((s) => s.id === id);

  if (!series) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-20 text-center">
            <div className="w-20 h-20 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-2">剧集未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，没有找到您要查看的剧集信息。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-full font-medium text-sm hover:bg-primary-600 transition-all whitespace-nowrap"
            >
              <i className="ri-home-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedSeries = tvSeriesData
    .filter((s) => s.id !== series.id && s.categorySlug === series.categorySlug)
    .slice(0, 6);

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main>
        <div className="relative w-full h-[360px] md:h-[420px] overflow-hidden">
          <img
            src={series.bannerImage}
            alt={`${series.title} - 红桃影院`}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/70"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 -mt-32 relative z-10">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: series.category, href: `/category/${series.categorySlug}` },
              { label: series.title },
            ]}
          />

          <div className="flex flex-col lg:flex-row gap-8 mt-6">
            <div className="flex-none w-[240px] md:w-[280px] mx-auto lg:mx-0">
              <div className="aspect-[2/3] rounded-xl overflow-hidden">
                <img
                  src={series.posterImage}
                  alt={`${series.title} - 红桃影院高清在线观看`}
                  title={`${series.title} ${series.category} ${series.year}`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 pt-4 lg:pt-8">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-white mb-2">
                {series.title}
              </h1>
              <p className="text-white/70 text-sm mb-4">{series.englishTitle}</p>

              <div className="flex items-center gap-3 mb-5 flex-wrap">
                <span className="px-3 py-1 bg-primary-500 text-white text-sm font-semibold rounded-md">
                  {series.rating}
                </span>
                <span className="px-3 py-1 bg-background-50/15 backdrop-blur-sm text-white text-sm rounded-md border border-white/20">
                  {series.year}
                </span>
                <span className="px-3 py-1 bg-background-50/15 backdrop-blur-sm text-white text-sm rounded-md border border-white/20">
                  {series.episodeCount}集
                </span>
                <span className="px-3 py-1 bg-background-50/15 backdrop-blur-sm text-white text-sm rounded-md border border-white/20">
                  {series.status}
                </span>
                <span className="px-3 py-1 bg-background-50/15 backdrop-blur-sm text-white text-sm rounded-md border border-white/20">
                  {series.region}
                </span>
              </div>

              <div className="flex items-center gap-2 mb-5 flex-wrap">
                {series.genre.map((g) => (
                  <span key={g} className="px-3 py-1 bg-accent-500/20 text-accent-300 text-xs font-medium rounded-full border border-accent-400/30">
                    {g}
                  </span>
                ))}
              </div>

              <p className="text-white/80 text-sm leading-relaxed max-w-2xl">
                {series.description}
              </p>

              <div className="flex items-center gap-3 mt-6">
                <span className="flex items-center gap-1 text-white/60 text-sm">
                  <i className="ri-eye-line"></i> {(series.viewCount / 10000).toFixed(1)}万次播放
                </span>
                <span className="text-white/40">|</span>
                <span className="flex items-center gap-1 text-white/60 text-sm">
                  <i className="ri-time-line"></i> {series.updateInfo}
                </span>
              </div>
            </div>
          </div>
        </div>

        <section className="py-12 md:py-16 px-4 md:px-6 lg:px-8 mt-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <article>
                  <h2 className="text-xl font-heading font-bold text-foreground-950 mb-5">剧情介绍</h2>
                  <div className="prose prose-sm max-w-none">
                    <p className="text-foreground-700 leading-relaxed whitespace-pre-line">
                      {series.longDescription}
                    </p>
                  </div>
                </article>

                <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-6">
                  <div>
                    <h4 className="text-xs font-medium text-foreground-500 uppercase tracking-wider mb-2">导演</h4>
                    <p className="text-sm font-medium text-foreground-900">{series.director}</p>
                  </div>
                  <div>
                    <h4 className="text-xs font-medium text-foreground-500 uppercase tracking-wider mb-2">语言</h4>
                    <p className="text-sm font-medium text-foreground-900">{series.language}</p>
                  </div>
                  <div>
                    <h4 className="text-xs font-medium text-foreground-500 uppercase tracking-wider mb-2">地区</h4>
                    <p className="text-sm font-medium text-foreground-900">{series.region}</p>
                  </div>
                </div>

                <div className="mt-8">
                  <h4 className="text-xs font-medium text-foreground-500 uppercase tracking-wider mb-3">主演</h4>
                  <div className="flex items-center gap-2 flex-wrap">
                    {series.cast.map((actor) => (
                      <span key={actor} className="px-3 py-1.5 bg-background-100 text-foreground-700 text-sm rounded-lg border border-background-200">
                        {actor}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <div className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                  <h3 className="text-lg font-heading font-bold text-foreground-950 mb-4">剧集信息</h3>
                  <dl className="space-y-4">
                    <div className="flex justify-between items-center">
                      <dt className="text-sm text-foreground-500">评分</dt>
                      <dd className="text-sm font-semibold text-primary-600">{series.rating}</dd>
                    </div>
                    <div className="flex justify-between items-center">
                      <dt className="text-sm text-foreground-500">年份</dt>
                      <dd className="text-sm font-semibold text-foreground-900">{series.year}</dd>
                    </div>
                    <div className="flex justify-between items-center">
                      <dt className="text-sm text-foreground-500">集数</dt>
                      <dd className="text-sm font-semibold text-foreground-900">{series.episodeCount}集</dd>
                    </div>
                    <div className="flex justify-between items-center">
                      <dt className="text-sm text-foreground-500">状态</dt>
                      <dd className="text-sm font-semibold text-foreground-900">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${series.status === '更新中' ? 'bg-accent-100 text-accent-700' : 'bg-secondary-100 text-secondary-700'}`}>
                          {series.status}
                        </span>
                      </dd>
                    </div>
                    <div className="flex justify-between items-center">
                      <dt className="text-sm text-foreground-500">地区</dt>
                      <dd className="text-sm font-semibold text-foreground-900">{series.region}</dd>
                    </div>
                    <div className="flex justify-between items-center">
                      <dt className="text-sm text-foreground-500">语言</dt>
                      <dd className="text-sm font-semibold text-foreground-900">{series.language}</dd>
                    </div>
                    <div className="flex justify-between items-center">
                      <dt className="text-sm text-foreground-500">播放量</dt>
                      <dd className="text-sm font-semibold text-foreground-900">{(series.viewCount / 10000).toFixed(1)}万</dd>
                    </div>
                  </dl>
                </div>

                <div className="mt-6 bg-background-100 rounded-xl p-6 border border-background-200/70">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-none">
                      <i className="ri-thumb-up-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">推荐指数</p>
                      <div className="flex items-center gap-0.5 mt-0.5">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <i
                            key={star}
                            className={`ri-star-fill text-xs ${star <= Math.round(series.rating / 2) ? 'text-accent-500' : 'text-foreground-300'}`}
                          ></i>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {relatedSeries.length > 0 && (
          <section className="py-12 md:py-16 px-4 md:px-6 lg:px-8 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
                {relatedSeries.map((rs) => (
                  <Link
                    key={rs.id}
                    to={`/article/${rs.id}`}
                    className="group cursor-pointer"
                    data-product-shop
                  >
                    <div className="relative aspect-[2/3] rounded-lg overflow-hidden mb-3">
                      <img
                        src={rs.posterImage}
                        alt={`${rs.title} - 红桃影院`}
                        title={`${rs.title} ${rs.category}`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-2 left-2">
                        <span className="px-2 py-0.5 bg-primary-500 text-white text-xs font-medium rounded-md">
                          {rs.rating}
                        </span>
                      </div>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors truncate">
                      {rs.title}
                    </h3>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />

      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: `${series.title} - 红桃影院`,
          description: series.description,
          author: { '@type': 'Organization', name: '红桃影院' },
          datePublished: `${series.year}-01-01`,
          image: series.posterImage,
          publisher: { '@type': 'Organization', name: '红桃影院', url: 'https://hongtao.tv' },
          mainEntityOfPage: { '@type': 'WebPage', '@id': `https://hongtao.tv/article/${series.id}` },
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hongtao.tv/' },
            { '@type': 'ListItem', position: 2, name: series.category, item: `https://hongtao.tv/category/${series.categorySlug}` },
            { '@type': 'ListItem', position: 3, name: series.title },
          ],
        })}
      </script>
    </>
  );
}